import React, { useState, useEffect, useRef } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Building2, Cpu, Phone, Mail, MapPin, FileText, ChevronLeft, Plus, X, Wifi, WifiOff, Search, Loader2 } from 'lucide-react';
import { getEmpresa, getDispositivos, updateDispositivo } from '../../lib/gestion4satApi';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function G4sEmpresaDetallePage() {
  const { empresaId } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Assign device dialog
  const [showAssign, setShowAssign] = useState(false);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignResults, setAssignResults] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [removing, setRemoving] = useState(null);
  const searchTimer = useRef(null);

  const loadEmpresa = async () => {
    try {
      const d = await getEmpresa(empresaId);
      setEmpresa(d);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  useEffect(() => { loadEmpresa(); }, [empresaId]);

  const searchDevices = (q) => {
    setAssignSearch(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.length < 2) { setAssignResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setAssignLoading(true);
      try {
        const res = await getDispositivos({ q, sin_empresa: true, limit: 15 });
        setAssignResults(res.dispositivos || []);
      } catch { setAssignResults([]); }
      setAssignLoading(false);
    }, 300);
  };

  const assignDevice = async (deviceId) => {
    setAssigning(deviceId);
    try {
      await updateDispositivo(deviceId, { empresa_id: empresaId, estado: 'activo' });
      toast.success('Dispositivo asignado');
      setAssignResults(r => r.filter(d => d.id !== deviceId));
      await loadEmpresa();
    } catch (e) { toast.error(e.message); }
    setAssigning(null);
  };

  const removeDevice = async (deviceId) => {
    setRemoving(deviceId);
    try {
      await updateDispositivo(deviceId, { empresa_id: '', estado: 'stock' });
      toast.success('Dispositivo desasignado');
      await loadEmpresa();
    } catch (e) { toast.error(e.message); }
    setRemoving(null);
  };

  const tipoLabel = { gps_solo: 'GPS Solo', gps_arriendo: 'GPS + Arriendo', camaras: 'Cámaras' };

  if (loading) return <G4sLayout><Skeleton className="h-96 w-full" /></G4sLayout>;
  if (!empresa) return <G4sLayout><p className="text-center py-12 text-muted-foreground">Empresa no encontrada</p></G4sLayout>;

  return (
    <G4sLayout>
      <div className="space-y-6" data-testid="g4s-empresa-detalle">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/gestion4sat/empresas')}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">{empresa.razon_social}</h1>
            <p className="text-sm text-muted-foreground font-mono">{empresa.rut_formateado}</p>
          </div>
          <Badge className={empresa.estado === 'activo' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}>
            {empresa.estado}
          </Badge>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {/* Info Card */}
          <Card className="md:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-emerald-500" /> Datos de Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div><div className="text-muted-foreground text-xs">Giro</div>{empresa.giro || '-'}</div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div><div className="text-muted-foreground text-xs">Dirección</div>{empresa.direccion || '-'}, {empresa.comuna}, {empresa.ciudad}</div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div><div className="text-muted-foreground text-xs">Teléfono</div>{empresa.telefono || '-'}</div>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="text-muted-foreground text-xs mb-1">Tipo de Servicio</div>
                <Badge variant="outline">{tipoLabel[empresa.tipo_servicio] || empresa.tipo_servicio}</Badge>
              </div>
              <div>
                <div className="text-muted-foreground text-xs mb-1">Mensualidad Neta</div>
                <span className="text-lg font-bold">${(empresa.precio_mensualidad || 0).toLocaleString('es-CL')}</span>
                <span className="text-xs text-muted-foreground ml-1">+ IVA</span>
              </div>
            </CardContent>
          </Card>

          {/* Contactos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-500" /> Contactos ({empresa.contactos?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {empresa.contactos?.length > 0 ? (
                <div className="space-y-3">
                  {empresa.contactos.map((c, i) => (
                    <div key={i} className="text-sm border-b pb-2 last:border-0 last:pb-0">
                      {c.nombre && <div className="font-medium">{c.nombre}</div>}
                      {c.email && <div className="text-muted-foreground text-xs">{c.email}</div>}
                      {c.telefono && <div className="text-muted-foreground text-xs">{c.telefono}</div>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Sin contactos registrados</p>
              )}
              {empresa.emails?.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-muted-foreground mb-1">Emails de facturación</div>
                  {empresa.emails.map((e, i) => (
                    <div key={i} className="text-xs font-mono text-blue-500">{e}</div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dispositivos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-teal-500" /> Dispositivos ({empresa.dispositivos_count || 0})
                </span>
                <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => { setShowAssign(true); setAssignSearch(''); setAssignResults([]); }} data-testid="btn-asignar-dispositivo">
                  <Plus className="h-3 w-3" /> Asignar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {empresa.dispositivos?.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {empresa.dispositivos.map(d => (
                    <div key={d.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0 group">
                      <div>
                        <div className="font-mono font-medium">{d.patente_formateada || 'Sin patente'}</div>
                        <div className="text-xs text-muted-foreground">IMEI: {d.imei}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={d.activity_status === 'Online' ? 'default' : 'secondary'}
                          className={d.activity_status === 'Online' ? 'bg-green-500/10 text-green-600' : ''}>
                          {d.activity_status === 'Online' ? <Wifi className="h-3 w-3 mr-1" /> : d.activity_status === 'Offline' ? <WifiOff className="h-3 w-3 mr-1" /> : null}
                          {d.activity_status || 'N/D'}
                        </Badge>
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeDevice(d.id)}
                          disabled={removing === d.id}
                          data-testid={`btn-remove-device-${d.imei}`}
                          title="Desasignar dispositivo"
                        >
                          {removing === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Sin dispositivos asignados</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assign Device Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar Dispositivo</DialogTitle>
            <DialogDescription>Busca dispositivos en stock para asignar a {empresa.razon_social}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por IMEI o patente..."
                value={assignSearch}
                onChange={e => searchDevices(e.target.value)}
                className="pl-9"
                data-testid="input-assign-search"
                autoFocus
              />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-1">
              {assignLoading ? (
                <div className="text-center py-4 text-sm text-muted-foreground">Buscando...</div>
              ) : assignResults.length === 0 ? (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  {assignSearch.length >= 2 ? 'Sin resultados en stock' : 'Escribe al menos 2 caracteres'}
                </div>
              ) : (
                assignResults.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-2 rounded hover:bg-muted text-sm">
                    <div>
                      <span className="font-mono font-medium">{d.imei}</span>
                      {d.patente_formateada && <span className="ml-2 text-muted-foreground">{d.patente_formateada}</span>}
                    </div>
                    <Button
                      size="sm" className="h-7 text-xs gap-1"
                      onClick={() => assignDevice(d.id)}
                      disabled={assigning === d.id}
                      data-testid={`btn-assign-${d.imei}`}
                    >
                      {assigning === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                      Asignar
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </G4sLayout>
  );
}
